"use client";

import { NAVIGATION_LINKS } from "@/config/constants";
import { fetchWorkflowById } from "@/app/(v2)/workflows/_hooks/workflowsApi";
import { executeQuery, getTableSchema, listTables } from "@/lib/api/org-database";
import { submitPendingPrompt } from "@/lib/workflow/submitPendingPrompt";
import { fetchStripeBillingDetails } from "@/hooks/useStripeBilling";
import { fetchSubscription } from "@/hooks/useSubscription";
import { useAIChatStore } from "@/stores/aiChatStore";
import { useCommandMenuStore } from "@/stores/commandMenuStore";
import { useConnectorsStore } from "@/stores/connectorsStore";
import { useNodeEditorStore } from "@/stores/nodeEditorStore";
import { useOrdersStore } from "@/stores/ordersStore";
import { useTemplatesStore } from "@/stores/templatesStore";
import { useWalletsStore } from "@/stores/walletsStore";
import { useWorkflowEditorStore } from "@/stores/workflowEditorStore";
import { useWorkflowRunsStore } from "@/stores/workflowRunsStore";
import { useWorkflowsStore, type Workflow } from "@/stores/workflowsStore";
import { defineVoiceTool } from "realtime-voice-component";
import { z } from "zod";
import {
  B3OS_VOICE_SELECT_WORKFLOW_NODE_EVENT,
  getVoiceCapabilitiesForPath,
  getVoiceRouteForPath,
  routeById,
  routeIdSchema,
  VOICE_CAPABILITIES,
  VOICE_ROUTES,
} from "./voiceCapabilities";
import { createAdminAnalyticsVoiceTools } from "./voiceAdminTools";
import {
  compactConnector,
  compactConnectorType,
  compactNode,
  compactRun,
  compactTemplate,
  compactWorkflow,
} from "./voiceCompact";
import {
  clampLimit,
  ensureConnectorStoresLoaded,
  getCurrentPathname,
  getCurrentWorkflowId,
  getTemplateMissingConfig,
  includesQuery,
  loadTemplates,
  normalizeText,
  resolveConnector,
  resolveConnectorType,
  resolveRun,
  resolveTemplate,
  resolveWorkflow,
  resolveWorkflowNode,
  searchWorkflows,
} from "./voiceEntityResolvers";
import { isReadOnlySql, withReadLimit } from "./voiceSql";
import { ensureOrgId, requireConfirmation } from "./voiceToolRuntime";
import {
  DASHBOARD_CADDIE_TARGET,
  DASHBOARD_PROMPT_TARGET,
  DASHBOARD_SUBMIT_TARGET,
  MAX_TOOL_ITEMS,
  templateCategorySchema,
  type B3OSVoiceToolsOptions,
  workflowRunStatusSchema,
  workflowStatusSchema,
  workflowViewSchema,
} from "./voiceToolTypes";
import { createWalletVoiceTools } from "./voiceWalletTools";
import { createWebhookVoiceTools } from "./voiceWebhooks";
import {
  createPublicWidgetVoiceTools,
  createWorkflowActionTool,
  createWorkflowEditorVoiceTools,
} from "./voiceWorkflowTools";

export type { B3OSVoiceToolsOptions, VoicePendingAction, VoicePendingConfirmation } from "./voiceToolTypes";

function getVoiceTarget<TElement extends HTMLElement = HTMLElement>(target: string): TElement | null {
  if (typeof document === "undefined") return null;
  return document.querySelector<TElement>(`[data-voice-target="${target}"]`);
}

function getPromptInput() {
  return getVoiceTarget<HTMLTextAreaElement>(DASHBOARD_PROMPT_TARGET);
}

function getPromptText() {
  return getPromptInput()?.value.trim() ?? "";
}

function setTextAreaValue(element: HTMLTextAreaElement, value: string) {
  const descriptor = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value");

  if (descriptor?.set) {
    descriptor.set.call(element, value);
  } else {
    element.value = value;
  }

  element.dispatchEvent(new Event("input", { bubbles: true }));
  element.dispatchEvent(new Event("change", { bubbles: true }));
}

function getNavigationTarget(href: string) {
  if (typeof document === "undefined") return null;
  return document.querySelector<HTMLAnchorElement>(`a[href="${href}"]`);
}

function isSafeLocalPath(path: string) {
  return path.startsWith("/") && !path.startsWith("//") && !/^[a-z][a-z0-9+.-]*:/i.test(path);
}

export function createB3OSVoiceTools(options: B3OSVoiceToolsOptions) {
  return [
    defineVoiceTool({
      name: "get_b3os_context",
      description:
        "Inspect current B3OS route, visible voice targets, platform state, selected organization, and available actions.",
      parameters: z.object({}),
      execute: () => {
        const aiState = useAIChatStore.getState();
        const commandState = useCommandMenuStore.getState();
        const connectorState = useConnectorsStore.getState();
        const runState = useWorkflowRunsStore.getState();
        const walletState = useWalletsStore.getState();
        const orderState = useOrdersStore.getState();
        const nodeEditorState = useNodeEditorStore.getState();
        const workflowEditorState = useWorkflowEditorStore.getState();
        const pathname = getCurrentPathname() || options.pathname;
        const activeRoute = getVoiceRouteForPath(pathname);
        const currentWorkflowId = getCurrentWorkflowId();

        return {
          ok: true,
          pathname,
          currentRoute: options.pathname,
          activeRoute,
          selectedOrganization: {
            id: options.selectedOrgId ?? null,
            name: options.selectedOrgName ?? null,
            currentRole: options.currentOrgRole ?? null,
            permissions: options.currentOrgPermissions ?? null,
          },
          dashboardPrompt: getPromptText(),
          dashboardPromptVisible: Boolean(getPromptInput()),
          caddiePanelOpen: aiState.isPanelOpen,
          caddiePanelMinimized: aiState.isPanelMinimized,
          commandMenuOpen: commandState.isOpen,
          workflowEditor: {
            currentWorkflowId,
            hasUnsavedChanges: workflowEditorState.hasUnsavedChanges,
            nodeEditorOpen: nodeEditorState.isOpen,
            selectedNodeId: nodeEditorState.selectedNodeId,
          },
          loadedStores: {
            connectorCount: connectorState.connectors.length,
            connectorTypeCount: connectorState.connectorTypes.length,
            currentRunCount: runState.runs.length,
            currentRunFilter: runState.currentStatus,
            walletCount: walletState.wallets.length,
            orderCount: orderState.orders.length,
          },
          pendingConfirmation: options.getPendingConfirmation(),
          recentConfirmedActions: options.getActionHistory().slice(0, 6),
          availableRoutes: VOICE_ROUTES,
          activeCapabilities: getVoiceCapabilitiesForPath(pathname),
          allCapabilities: VOICE_CAPABILITIES,
          navigationLabels: NAVIGATION_LINKS.map(link => link.label),
        };
      },
    }),
    defineVoiceTool({
      name: "confirm_voice_action",
      description:
        "Confirm and execute the pending risky voice action after the user verbally agrees or clicks the modal confirm button.",
      parameters: z.object({
        token: z
          .string()
          .optional()
          .describe("Optional confirmation token. Omit to confirm the current pending action."),
      }),
      execute: async ({ token }) => options.confirmPendingAction(token),
    }),
    defineVoiceTool({
      name: "cancel_voice_action",
      description: "Cancel the pending risky voice action after the user declines or asks to stop.",
      parameters: z.object({
        token: z
          .string()
          .optional()
          .describe("Optional confirmation token. Omit to cancel the current pending action."),
      }),
      execute: ({ token }) => {
        const cancelled = options.cancelPendingAction(token);
        return cancelled
          ? { ok: true, cancelled }
          : { ok: false, reason: "No pending voice action matched that token." };
      },
    }),
    defineVoiceTool({
      name: "set_dashboard_prompt",
      description: "Replace the dashboard Caddie prompt text without submitting it.",
      parameters: z.object({
        prompt: z.string().min(1).describe("The exact prompt text to place in the dashboard input."),
      }),
      execute: async ({ prompt }) => {
        const input = getPromptInput();

        if (!input) {
          return {
            ok: false,
            reason: "The dashboard prompt is not visible. Navigate to the dashboard before setting it.",
          };
        }

        await options.run({ element: input }, () => {
          setTextAreaValue(input, prompt);
          input.focus();
        });

        return { ok: true, prompt };
      },
    }),
    defineVoiceTool({
      name: "append_dashboard_prompt",
      description: "Append words to the existing dashboard prompt without submitting it.",
      parameters: z.object({
        text: z.string().min(1).describe("Text to append to the dashboard prompt."),
      }),
      execute: async ({ text }) => {
        const input = getPromptInput();

        if (!input) {
          return {
            ok: false,
            reason: "The dashboard prompt is not visible. Navigate to the dashboard before appending to it.",
          };
        }

        const nextPrompt = [input.value.trim(), text.trim()].filter(Boolean).join(" ");
        await options.run({ element: input }, () => {
          setTextAreaValue(input, nextPrompt);
          input.focus();
        });

        return { ok: true, prompt: nextPrompt };
      },
    }),
    defineVoiceTool({
      name: "submit_dashboard_prompt",
      description: "Submit a natural-language request to Caddie so B3OS starts building or editing a workflow.",
      parameters: z.object({
        prompt: z
          .string()
          .min(1)
          .optional()
          .describe("Optional prompt to submit. If omitted, submits the text already in the dashboard input."),
      }),
      execute: async ({ prompt }) => {
        const input = getPromptInput();
        const submitButton = getVoiceTarget<HTMLButtonElement>(DASHBOARD_SUBMIT_TARGET);
        const promptToSubmit = (prompt ?? getPromptText()).trim();

        if (!promptToSubmit) {
          return {
            ok: false,
            reason: "No prompt was provided and the dashboard prompt is empty.",
          };
        }

        if (input && prompt && input.value.trim() !== promptToSubmit) {
          setTextAreaValue(input, promptToSubmit);
        }

        await options.run({ element: submitButton ?? input }, () => {
          submitPendingPrompt(promptToSubmit, options.router);
        });

        return { ok: true, prompt: promptToSubmit, destination: "/workflows/edit" };
      },
    }),
    defineVoiceTool({
      name: "focus_dashboard_prompt",
      description: "Focus the dashboard prompt so the user can continue typing.",
      parameters: z.object({}),
      execute: async () => {
        const input = getPromptInput();

        if (!input) {
          return { ok: false, reason: "The dashboard prompt is not visible." };
        }

        await options.run({ element: input }, () => {
          input.focus();
        });

        return { ok: true };
      },
    }),
    defineVoiceTool({
      name: "navigate_b3os_route",
      description: "Navigate to a known B3OS app route.",
      parameters: z.object({
        route: routeIdSchema.describe("The route id to open."),
      }),
      execute: async ({ route }) => {
        const targetRoute = routeById.get(route);

        if (!targetRoute) {
          return { ok: false, reason: `Unknown route: ${route}` };
        }

        await options.run({ element: getNavigationTarget(targetRoute.href) }, () => {
          options.router.push(targetRoute.href);
        });

        return { ok: true, route: targetRoute };
      },
    }),
    defineVoiceTool({
      name: "navigate_b3os_path",
      description: "Navigate to a safe local B3OS path, including dynamic entity paths generated by other tools.",
      parameters: z.object({
        path: z.string().min(1).describe("A local path beginning with slash."),
      }),
      execute: async ({ path }) => {
        if (!isSafeLocalPath(path)) {
          return { ok: false, reason: "Only safe local B3OS paths are allowed." };
        }

        await options.run({ element: getNavigationTarget(path) }, () => {
          options.router.push(path);
        });

        return { ok: true, path };
      },
    }),
    defineVoiceTool({
      name: "open_command_menu",
      description: "Open the B3OS command menu for search and quick actions.",
      parameters: z.object({}),
      execute: async () => {
        await options.run(
          { element: document.querySelector<HTMLElement>('[data-voice-target="command-menu-entry"]') },
          () => {
            useCommandMenuStore.getState().setOpen(true);
          },
        );

        return { ok: true };
      },
    }),
    defineVoiceTool({
      name: "open_caddie_panel",
      description: "Open the Caddie AI chat panel.",
      parameters: z.object({}),
      execute: async () => {
        await options.run({ element: getVoiceTarget(DASHBOARD_CADDIE_TARGET) }, () => {
          useAIChatStore.getState().openPanel();
        });

        return { ok: true };
      },
    }),
    defineVoiceTool({
      name: "show_voice_message",
      description: "Show a short visible message to the user when no other UI action is needed.",
      parameters: z.object({
        message: z.string().min(1).max(160),
      }),
      execute: ({ message }) => {
        options.pushNotice("info", "Caddie", message);
        return { ok: true, message };
      },
    }),
    defineVoiceTool({
      name: "search_workflows",
      description: "Search workflows by name, id, description, or status.",
      parameters: z.object({
        query: z.string().optional(),
        status: workflowStatusSchema.optional(),
        limit: z.number().int().positive().max(MAX_TOOL_ITEMS).optional(),
      }),
      execute: async ({ query, status, limit }) => {
        const workflows = await searchWorkflows({ query, status, limit });
        return { ok: true, workflows: workflows.map(compactWorkflow), count: workflows.length };
      },
    }),
    defineVoiceTool({
      name: "open_workflow",
      description: "Resolve and open a workflow in the workflow editor.",
      parameters: z.object({
        workflow: z.string().describe("Workflow id or spoken workflow name."),
        view: workflowViewSchema.optional(),
      }),
      execute: async ({ workflow, view }) => {
        const resolved = await resolveWorkflow(workflow);
        if (!resolved.ok) return resolved;
        const params = new URLSearchParams({ id: resolved.item.id });
        if (view) params.set("view", view);
        const path = `/workflows/edit?${params.toString()}`;
        await options.run({ element: getNavigationTarget("/workflows") }, () => {
          options.router.push(path);
        });
        return { ok: true, path, workflow: compactWorkflow(resolved.item) };
      },
    }),
    defineVoiceTool({
      name: "get_workflow_details",
      description: "Get detailed workflow metadata, inputs, connectors, and node summaries.",
      parameters: z.object({
        workflow: z
          .string()
          .optional()
          .describe("Workflow id or spoken workflow name. Omit to use the current workflow."),
      }),
      execute: async ({ workflow }) => {
        const resolved = await resolveWorkflow(workflow);
        if (!resolved.ok) return resolved;
        const full = resolved.item.definition ? resolved.item : await fetchWorkflowById(resolved.item.id);
        const nodes = Object.entries(full.definition?.nodes ?? {}).map(([nodeId, node]) => compactNode(nodeId, node));
        return {
          ok: true,
          workflow: compactWorkflow(full),
          inputs: full.definition?.inputSchema ?? [],
          nodes,
          connectors: full.connectors,
          x402Config: full.x402Config,
          publicWidgetConfig: full.publicWidgetConfig,
        };
      },
    }),
    createWorkflowActionTool(options),
    ...createWorkflowEditorVoiceTools(options),
    ...createPublicWidgetVoiceTools(options),
    defineVoiceTool({
      name: "list_workflow_runs",
      description: "List recent workflow runs, optionally for one workflow or status.",
      parameters: z.object({
        workflow: z.string().optional().describe("Workflow id or spoken name."),
        status: workflowRunStatusSchema.optional(),
        limit: z.number().int().positive().max(MAX_TOOL_ITEMS).optional(),
      }),
      execute: async ({ workflow, status, limit }) => {
        let workflowId: string | undefined;
        if (workflow) {
          const resolved = await resolveWorkflow(workflow);
          if (!resolved.ok) return resolved;
          workflowId = resolved.item.id;
        }
        const store = useWorkflowRunsStore.getState();
        await store.loadRunsPage(0, workflowId, status);
        const runs = useWorkflowRunsStore.getState().runs.slice(0, clampLimit(limit));
        return { ok: true, workflowId, runs: runs.map(compactRun), count: runs.length };
      },
    }),
    defineVoiceTool({
      name: "get_workflow_run_details",
      description: "Fetch one workflow run with node execution state.",
      parameters: z.object({
        run: z.string().optional().describe("Run id, or say latest to use the latest loaded run."),
        workflow: z.string().optional().describe("Optional workflow id or name to scope latest run lookup."),
      }),
      execute: async ({ run, workflow }) => {
        let workflowId: string | undefined;
        if (workflow) {
          const resolvedWorkflow = await resolveWorkflow(workflow);
          if (!resolvedWorkflow.ok) return resolvedWorkflow;
          workflowId = resolvedWorkflow.item.id;
        }
        const resolved = await resolveRun(run, workflowId);
        if (!resolved.ok) return resolved;
        return {
          ok: true,
          run: compactRun(resolved.item),
          executionState: resolved.item.executionState,
          workflowDefinition: resolved.item.workflowDefinition,
        };
      },
    }),
    defineVoiceTool({
      name: "open_workflow_run",
      description: "Open a workflow run detail page.",
      parameters: z.object({
        run: z.string().describe("Run id, or latest."),
        workflow: z.string().optional(),
      }),
      execute: async ({ run, workflow }) => {
        let workflowId: string | undefined;
        if (workflow) {
          const resolvedWorkflow = await resolveWorkflow(workflow);
          if (!resolvedWorkflow.ok) return resolvedWorkflow;
          workflowId = resolvedWorkflow.item.id;
        }
        const resolved = await resolveRun(run, workflowId);
        if (!resolved.ok) return resolved;
        const path = `/workflows/history/${encodeURIComponent(resolved.item.id)}`;
        await options.run({ element: getNavigationTarget("/workflows/history") }, () => {
          options.router.push(path);
        });
        return { ok: true, path, run: compactRun(resolved.item) };
      },
    }),
    defineVoiceTool({
      name: "workflow_run_action",
      description: "Cancel or retry a workflow run. Requires confirmation.",
      parameters: z.object({
        action: z.enum(["cancel", "retry"]),
        run: z.string().describe("Run id, or latest."),
        workflow: z.string().optional().describe("Optional workflow id or name to scope latest run lookup."),
      }),
      execute: async ({ action, run, workflow }) => {
        let workflowId: string | undefined;
        if (workflow) {
          const resolvedWorkflow = await resolveWorkflow(workflow);
          if (!resolvedWorkflow.ok) return resolvedWorkflow;
          workflowId = resolvedWorkflow.item.id;
        }

        const resolved = await resolveRun(run, workflowId);
        if (!resolved.ok) return resolved;
        const targetRun = resolved.item;

        return requireConfirmation(options, {
          action: `workflow_run_action:${action}`,
          description: `${action} workflow run ${targetRun.id}`,
          details: { action, run: compactRun(targetRun) },
          risk: action === "cancel" ? "destructive" : "write",
          title: "Confirm workflow run action",
          successMessage: `Completed: ${action} run ${targetRun.id}`,
          execute: async () => {
            const store = useWorkflowRunsStore.getState();
            if (action === "cancel") {
              await store.cancelRun(targetRun.id);
              return { ok: true, action, run: compactRun({ ...targetRun, status: "cancelled" }) };
            }
            const retried = await store.retryRun(targetRun.id);
            return { ok: true, action, originalRun: compactRun(targetRun), retriedRun: retried };
          },
        });
      },
    }),
    defineVoiceTool({
      name: "list_connectors",
      description: "List configured connectors and available connector types.",
      parameters: z.object({
        query: z.string().optional(),
        type: z.string().optional(),
        configuredOnly: z.boolean().optional(),
        limit: z.number().int().positive().max(MAX_TOOL_ITEMS).optional(),
      }),
      execute: async ({ query, type, configuredOnly, limit }) => {
        const store = await ensureConnectorStoresLoaded();
        const max = clampLimit(limit);
        const connectors = store.connectors
          .filter(
            connector =>
              (!type || normalizeText(connector.type) === normalizeText(type)) &&
              includesQuery([connector.id, connector.name, connector.type, connector.description], query),
          )
          .slice(0, max);
        const connectorTypes = configuredOnly
          ? []
          : store.connectorTypes
              .filter(
                connectorType =>
                  (!type || normalizeText(connectorType.type) === normalizeText(type)) &&
                  includesQuery(
                    [connectorType.type, connectorType.name, connectorType.description, connectorType.provider],
                    query,
                  ),
              )
              .slice(0, max);

        return {
          ok: true,
          connectors: connectors.map(compactConnector),
          connectorTypes: connectorTypes.map(compactConnectorType),
          activeTab: store.activeTab,
        };
      },
    }),
    defineVoiceTool({
      name: "open_connector_setup",
      description: "Open the connectors page and focus either configured or available connectors.",
      parameters: z.object({
        tab: z.enum(["available", "configured"]).optional(),
        connector: z.string().optional().describe("Optional connector type or name to mention in the result."),
      }),
      execute: async ({ tab = "available", connector }) => {
        useConnectorsStore.getState().setActiveTab(tab);
        await options.run({ element: getNavigationTarget("/connectors") }, () => {
          options.router.push("/connectors");
        });
        return { ok: true, destination: "/connectors", tab, connector };
      },
    }),
    defineVoiceTool({
      name: "connector_action",
      description:
        "Create, update, or delete a configured connector. OAuth connectors are opened in the UI instead of automated.",
      parameters: z.object({
        action: z.enum(["create", "update", "delete"]),
        connector: z.string().optional().describe("Connector id or name for update/delete."),
        type: z.string().optional().describe("Connector type for create."),
        name: z.string().optional(),
        description: z.string().optional(),
        data: z.record(z.string(), z.unknown()).optional(),
      }),
      execute: async ({ action, connector, type, name, description, data }) => {
        const store = await ensureConnectorStoresLoaded();

        if (action === "create") {
          if (!type || !name || !data) {
            return { ok: false, reason: "Creating a connector requires type, name, and data." };
          }
          const resolvedType = await resolveConnectorType(type);
          if (!resolvedType.ok) return resolvedType;
          if (resolvedType.item.providerConfig?.authType === "oauth") {
            useConnectorsStore.getState().setActiveTab("available");
            options.router.push("/connectors");
            return {
              ok: false,
              reason: "OAuth connector setup requires the interactive authorization flow. Opened the connectors page.",
              connectorType: compactConnectorType(resolvedType.item),
            };
          }

          return requireConfirmation(options, {
            action: "connector_action:create",
            description: `Create ${resolvedType.item.name} connector "${name}"`,
            details: { type: resolvedType.item.type, name, description, dataKeys: Object.keys(data) },
            risk: "write",
            title: "Confirm connector creation",
            successMessage: `Created connector ${name}`,
            execute: async () => {
              const created = await store.createConnector({
                type: resolvedType.item.type,
                name,
                description: description ?? "",
                data,
              });
              return { ok: true, action, connector: compactConnector(created) };
            },
          });
        }

        if (!connector) {
          return { ok: false, reason: `${action} requires a connector id or name.` };
        }
        const resolved = await resolveConnector(connector);
        if (!resolved.ok) return resolved;

        return requireConfirmation(options, {
          action: `connector_action:${action}`,
          description: `${action} connector "${resolved.item.name}"`,
          details: {
            connector: compactConnector(resolved.item),
            update: { name, description, dataKeys: Object.keys(data ?? {}) },
          },
          risk: action === "delete" ? "destructive" : "write",
          title: "Confirm connector action",
          successMessage: `Completed: ${action} connector ${resolved.item.name}`,
          execute: async () => {
            if (action === "delete") {
              await store.deleteConnector(resolved.item.id);
              return { ok: true, action, connector: compactConnector(resolved.item) };
            }
            const updated = await store.updateConnector(resolved.item.id, {
              ...(name ? { name } : {}),
              ...(description !== undefined ? { description } : {}),
              ...(data ? { data } : {}),
            });
            return { ok: true, action, connector: compactConnector(updated) };
          },
        });
      },
    }),
    defineVoiceTool({
      name: "search_templates",
      description: "Search B3OS workflow templates.",
      parameters: z.object({
        query: z.string().optional(),
        category: templateCategorySchema.optional(),
        tags: z.array(z.string()).optional(),
        limit: z.number().int().positive().max(MAX_TOOL_ITEMS).optional(),
      }),
      execute: async ({ query, category = "all", tags, limit }) => {
        const templates = await loadTemplates(category, tags);
        const matches = templates
          .filter(template =>
            includesQuery(
              [
                template.id,
                template.slug,
                template.name,
                template.description,
                ...(template.tags ?? []).flatMap(tag => [tag.name, tag.slug]),
              ],
              query,
            ),
          )
          .slice(0, clampLimit(limit));
        return { ok: true, templates: matches.map(compactTemplate), count: matches.length };
      },
    }),
    defineVoiceTool({
      name: "open_template",
      description: "Resolve and open a template detail page.",
      parameters: z.object({
        template: z.string().describe("Template id, slug, or spoken name."),
        category: templateCategorySchema.optional(),
      }),
      execute: async ({ template, category = "all" }) => {
        const resolved = await resolveTemplate(template, category);
        if (!resolved.ok) return resolved;
        const id = resolved.item.slug || resolved.item.id;
        const path = `/templates/${encodeURIComponent(id)}`;
        await options.run({ element: getNavigationTarget("/templates") }, () => {
          options.router.push(path);
        });
        return { ok: true, path, template: compactTemplate(resolved.item) };
      },
    }),
    defineVoiceTool({
      name: "template_action",
      description:
        "Instantiate or manage templates. Creating from templates only automates templates without missing required fields/connectors.",
      parameters: z.object({
        action: z.enum(["create_workflow", "promote", "unpromote", "delete"]),
        template: z.string().describe("Template id, slug, or spoken name."),
        category: templateCategorySchema.optional(),
      }),
      execute: async ({ action, template, category = "all" }) => {
        const resolved = await resolveTemplate(template, category);
        if (!resolved.ok) return resolved;
        const target = resolved.item;

        if (action === "create_workflow") {
          const missing = getTemplateMissingConfig(target);
          if (missing.missingRequiredProps.length > 0 || missing.missingConnectorTypes.length > 0) {
            const path = `/templates/${encodeURIComponent(target.slug || target.id)}`;
            options.router.push(path);
            return {
              ok: false,
              reason: "This template needs interactive configuration before voice can create it.",
              template: compactTemplate(target),
              missing,
              destination: path,
            };
          }
        }

        return requireConfirmation(options, {
          action: `template_action:${action}`,
          description: `${action} template "${target.name}"`,
          details: { action, template: compactTemplate(target) },
          risk: action === "delete" ? "destructive" : "write",
          title: "Confirm template action",
          successMessage: `Completed: ${action} template ${target.name}`,
          execute: async () => {
            const store = useTemplatesStore.getState();
            if (action === "create_workflow") {
              const workflowRequest = store.templateToWorkflowRequest(target);
              const workflow = await useWorkflowsStore.getState().createWorkflow(workflowRequest);
              options.router.push(`/workflows/edit?id=${encodeURIComponent(workflow.id)}`);
              return { ok: true, action, template: compactTemplate(target), workflow: compactWorkflow(workflow) };
            }
            if (action === "promote") {
              await store.promoteTemplate(target.id);
              return { ok: true, action, template: compactTemplate(target) };
            }
            if (action === "unpromote") {
              await store.unpromoteTemplate(target.id);
              return { ok: true, action, template: compactTemplate(target) };
            }
            await store.deleteTemplate(target.id);
            return { ok: true, action, template: compactTemplate(target) };
          },
        });
      },
    }),
    defineVoiceTool({
      name: "list_database_tables",
      description: "List organization database tables.",
      parameters: z.object({}),
      execute: async () => {
        const orgId = ensureOrgId(options);
        const response = await listTables(orgId);
        return { ok: true, tables: response.data.tables };
      },
    }),
    defineVoiceTool({
      name: "describe_database_table",
      description: "Describe a database table schema.",
      parameters: z.object({
        table: z.string().min(1),
      }),
      execute: async ({ table }) => {
        const orgId = ensureOrgId(options);
        const response = await getTableSchema(orgId, table);
        return { ok: true, table, columns: response.data.columns };
      },
    }),
    defineVoiceTool({
      name: "query_database_readonly",
      description: "Run a read-only SQL query against the organization database with a row limit.",
      parameters: z.object({
        sql: z.string().min(1),
        params: z.array(z.unknown()).optional(),
        limit: z.number().int().positive().max(100).optional(),
      }),
      execute: async ({ sql, params, limit }) => {
        const orgId = ensureOrgId(options);
        if (!isReadOnlySql(sql)) {
          return {
            ok: false,
            reason: "Only read-only SELECT, WITH, and EXPLAIN queries are allowed through voice control.",
          };
        }
        const limitedSql = withReadLimit(sql, clampLimit(limit, 20));
        const response = await executeQuery(orgId, limitedSql, params);
        return { ok: true, sql: limitedSql, result: response.data };
      },
    }),
    defineVoiceTool({
      name: "get_workflow_editor_context",
      description: "Inspect the current workflow editor state and current workflow nodes.",
      parameters: z.object({}),
      execute: async () => {
        const currentWorkflowId = getCurrentWorkflowId();
        const workflowEditorState = useWorkflowEditorStore.getState();
        const nodeEditorState = useNodeEditorStore.getState();
        let workflow: Workflow | null = null;
        if (currentWorkflowId) {
          workflow = await fetchWorkflowById(currentWorkflowId).catch(() => null);
        }
        return {
          ok: true,
          currentWorkflowId,
          hasUnsavedChanges: workflowEditorState.hasUnsavedChanges,
          nodeEditorOpen: nodeEditorState.isOpen,
          selectedNodeId: nodeEditorState.selectedNodeId,
          workflow: workflow ? compactWorkflow(workflow) : null,
          nodes: workflow
            ? Object.entries(workflow.definition?.nodes ?? {}).map(([nodeId, node]) => compactNode(nodeId, node))
            : [],
        };
      },
    }),
    defineVoiceTool({
      name: "focus_workflow_node",
      description: "Resolve a node in the current workflow and open/focus its node editor state when possible.",
      parameters: z.object({
        node: z.string().min(1).describe("Node id, type, title, or description."),
      }),
      execute: async ({ node }) => {
        const currentWorkflowId = getCurrentWorkflowId();
        if (!currentWorkflowId) {
          return { ok: false, reason: "No workflow editor id is present in the current URL." };
        }
        const workflow = await fetchWorkflowById(currentWorkflowId);
        const resolved = resolveWorkflowNode(workflow, node);
        if (!resolved.ok) return resolved;
        window.dispatchEvent(
          new CustomEvent(B3OS_VOICE_SELECT_WORKFLOW_NODE_EVENT, {
            detail: { nodeId: resolved.item.id },
          }),
        );
        options.pushNotice("info", "Node selected", resolved.item.node.titleOverride || resolved.item.node.type);
        return { ok: true, node: compactNode(resolved.item.id, resolved.item.node) };
      },
    }),
    ...createWalletVoiceTools(options),
    defineVoiceTool({
      name: "get_billing_summary",
      description:
        "Fetch read-only billing, subscription, CU usage, payment method, and invoice summary for the selected organization.",
      parameters: z.object({}),
      execute: async () => {
        const orgId = ensureOrgId(options);
        const [subscription, stripe] = await Promise.all([
          fetchSubscription(orgId),
          fetchStripeBillingDetails(orgId).catch(() => null),
        ]);
        return {
          ok: true,
          organizationId: orgId,
          subscription,
          stripeBilling: stripe
            ? {
                paymentMethodCount: stripe.paymentMethods.length,
                defaultPaymentMethod: stripe.paymentMethods.find(method => method.isDefault) ?? null,
                invoices: stripe.invoices.slice(0, 6).map(invoice => ({
                  id: invoice.id,
                  number: invoice.number,
                  status: invoice.status,
                  total: invoice.total,
                  amountDue: invoice.amountDue,
                  amountPaid: invoice.amountPaid,
                  createdAt: invoice.createdAt,
                  hostedInvoiceUrl: invoice.hostedInvoiceUrl,
                  invoicePdf: invoice.invoicePdf,
                })),
              }
            : null,
        };
      },
    }),
    defineVoiceTool({
      name: "start_billing_flow",
      description:
        "Open the billing page for plan changes, payment method changes, or invoice download actions that should remain in the UI.",
      parameters: z.object({
        intent: z.string().optional().describe("User intent, such as upgrade plan or add payment method."),
      }),
      execute: async ({ intent }) => {
        await options.run({ element: getNavigationTarget("/billing") }, () => {
          options.router.push("/billing");
        });
        return {
          ok: true,
          destination: "/billing",
          intent,
          reason: "Billing changes remain in the interactive billing UI.",
        };
      },
    }),
    ...createWebhookVoiceTools(options),
    ...createAdminAnalyticsVoiceTools(options),
  ];
}
